import React from "react";
import { Link } from "react-router-dom";
import {
  FaTshirt,
  FaUserTie,
  FaCut,
  FaBox,
  FaCheck,
  FaArrowRight,
} from "react-icons/fa";

const services = [
  {
    icon: FaTshirt,
    title: "T-Shirt Manufacturing",
    desc: "Stylish and comfortable t-shirts with premium fabric quality and custom branding options.",
    features: [
      "100% cotton & blended fabrics",
      "Custom prints & embroidery",
      "MOQ from 500 units",
    ],
    color: "bg-blue-50 text-blue-700",
  },
  {
    icon: FaUserTie,
    title: "Formal Wear",
    desc: "Professional men formal garments designed with precision for corporate and office environments.",
    features: [
      "Corporate & office wear",
      "Custom measurements",
      "Bulk pricing available",
    ],
    color: "bg-indigo-50 text-indigo-700",
  },
  {
    icon: FaCut,
    title: "Custom Stitching",
    desc: "Customized garment stitching for bulk and corporate orders with design-to-delivery service.",
    features: ["Design-to-delivery", "Any fabric type", "Express turnaround"],
    color: "bg-sky-50 text-sky-700",
  },
  {
    icon: FaBox,
    title: "Bulk Supply",
    desc: "Large-scale production and supply for fashion businesses across India and internationally.",
    features: [
      "Pan-India delivery",
      "Export-ready packaging",
      "Quality certification",
    ],
    color: "bg-teal-50 text-teal-700",
  },
];

const advantages = [
  "20+ years of garment manufacturing experience",
  "1,500–1,800 skilled workers in-house",
  "Advanced machinery and quality control",
  "Bulk order capabilities with fast turnaround",
  "ISO-aligned quality assurance processes",
  "Export-ready packaging and compliance",
];

const processSteps = [
  {
    step: "3530",
    title: "Enquiry",
    desc: "Submit your requirements, quantities, and design specifications to our team.",
  },
  {
    step: "230",
    title: "Sample",
    desc: "We produce a sample for your approval before full-scale production begins.",
  },
  {
    step: "530",
    title: "Production",
    desc: "Our skilled workforce manufactures your order with strict quality controls.",
  },
  {
    step: "1520",
    title: "Delivery",
    desc: "Timely dispatch and delivery to your location, export-ready if required.",
  },
];

const Service = () => {
  return (
    <>
      {/* ── Page Hero ── */}
      <section className="relative h-[480px] overflow-hidden">
        <img
          src="/images/banner.png"
          alt="Our Services"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <span className="inline-block bg-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest text-white mb-5">
              Om Maruticreation
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Our Services
            </h1>
          </div>
        </div>
      </section>

      {/* ── Services Grid ── */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              What We Offer
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Manufacturing Solutions
            </h2>
            <p className="mt-4 text-gray-500 text-lg">
              Complete garment manufacturing solutions for fashion brands,
              wholesalers, and retailers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300 p-8 hover:border-blue-200 hover:-translate-y-1"
                >
                  <div className="flex items-start gap-5">
                    <div
                      className={`h-14 w-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 ${service.color}`}
                    >
                      <Icon />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">
                        {service.title}
                      </h3>
                      <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                        {service.desc}
                      </p>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-2">
                    {service.features.map((feat, fi) => (
                      <li
                        key={fi}
                        className="flex items-center gap-3 text-sm text-gray-600"
                      >
                        <FaCheck className="text-green-500 flex-shrink-0" />
                        {feat}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className="mt-6 inline-flex items-center gap-1 text-blue-700 font-semibold text-sm hover:gap-2 transition-all"
                  >
                    Enquire <FaArrowRight className="text-xs" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: dark blue panel */}
          <div className="bg-blue-700 rounded-3xl p-10 text-white">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-300">
              Why Us
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold leading-tight">
              Trusted By 500+ Brands Across India
            </h2>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {[
                { num: "20+", label: "Years Experience" },
                { num: "1800", label: "Skilled Workers" },
                { num: "100K+", label: "Garments / Year" },
                { num: "500+", label: "Happy Clients" },
              ].map((s, i) => (
                <div key={i} className="bg-white/10 rounded-2xl p-5">
                  <p className="text-3xl font-bold">{s.num}</p>
                  <p className="text-blue-200 text-sm mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: advantage list */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              Our Advantages
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold text-gray-900">
              What Sets Us Apart
            </h2>
            <ul className="mt-8 space-y-4">
              {advantages.map((adv, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="bg-blue-700 text-white h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FaCheck className="text-xs" />
                  </div>
                  <span className="text-gray-600">{adv}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Process Timeline ── */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              How It Works
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Our Process
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, i) => (
              <div key={i} className="relative">
                {/* Connector line */}
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+2rem)] w-full h-px bg-blue-100 z-0" />
                )}
                <div className="relative z-10 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center hover:shadow-lg transition-shadow duration-300">
                  <div className="h-16 w-16 bg-blue-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 text-2xl">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 lg:py-24 bg-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            Ready to Start Your Order?
          </h2>
          <p className="mt-5 text-blue-200 text-lg max-w-xl mx-auto">
            Contact OM Maruthi Creations today for a free consultation and
            custom quote for your garment requirements.
          </p>
          <div className="mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-4 rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Get In Touch <FaArrowRight className="text-sm" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Service;
